/**
 * useDraggable Hook
 *
 * Provides pointer-based drag functionality for Mac Studio windows.
 * Uses transform for GPU-accelerated movement.
 */

import { useCallback, useRef, useEffect, useState } from 'react'

interface Position {
  x: number
  y: number
}

interface UseDraggableOptions {
  /** Initial position */
  initialPosition?: Position
  /** Called when drag starts */
  onDragStart?: (position: Position) => void
  /** Called during drag */
  onDrag?: (position: Position) => void
  /** Called when drag ends */
  onDragEnd?: (position: Position) => void
  /** Called when element is clicked (for focus) */
  onFocus?: () => void
  /** Constrain to viewport bounds */
  constrainToViewport?: boolean
  /** Minimum inset from viewport edges */
  viewportInset?: number
  /** Whether dragging is enabled */
  enabled?: boolean
}

interface UseDraggableReturn {
  /** Props to spread on the draggable handle element */
  dragHandleProps: {
    onPointerDown: (e: React.PointerEvent) => void
    style: React.CSSProperties
  }
  /** Props to spread on the draggable container element */
  containerProps: {
    ref: React.RefObject<HTMLDivElement | null>
    style: React.CSSProperties
  }
  /** Current position */
  position: Position
  /** Whether currently dragging */
  isDragging: boolean
  /** Set position programmatically */
  setPosition: (position: Position) => void
}

export function useDraggable({
  initialPosition = { x: 0, y: 0 },
  onDragStart,
  onDrag,
  onDragEnd,
  onFocus,
  constrainToViewport = true,
  viewportInset = 50,
  enabled = true,
}: UseDraggableOptions = {}): UseDraggableReturn {
  const containerRef = useRef<HTMLDivElement>(null)
  const [position, setPosition] = useState<Position>(initialPosition)
  const [isDragging, setIsDragging] = useState(false)

  // Store drag state in refs to avoid stale closures
  const dragStateRef = useRef({
    startX: 0,
    startY: 0,
    startPosX: 0,
    startPosY: 0,
  })

  // Store latest values in refs to avoid stale closures in event listeners
  const enabledRef = useRef(enabled)
  const onDragRef = useRef(onDrag)
  const onDragEndRef = useRef(onDragEnd)
  const constrainPositionRef = useRef<(pos: Position) => Position>(() => initialPosition)

  // Keep refs updated
  useEffect(() => {
    enabledRef.current = enabled
  }, [enabled])

  useEffect(() => {
    onDragRef.current = onDrag
  }, [onDrag])

  useEffect(() => {
    onDragEndRef.current = onDragEnd
  }, [onDragEnd])

  // Update position when initialPosition changes (external updates)
  useEffect(() => {
    setPosition(initialPosition)
  }, [initialPosition.x, initialPosition.y])

  // Constrain position to viewport
  const constrainPosition = useCallback(
    (pos: Position): Position => {
      if (!constrainToViewport || typeof window === 'undefined') return pos

      const container = containerRef.current
      const containerWidth = container?.offsetWidth ?? 0

      const maxX = window.innerWidth - viewportInset
      const maxY = window.innerHeight - viewportInset
      const minX = viewportInset - containerWidth + viewportInset
      const minY = viewportInset

      return {
        x: Math.max(minX, Math.min(maxX, pos.x)),
        y: Math.max(minY, Math.min(maxY, pos.y)),
      }
    },
    [constrainToViewport, viewportInset]
  )

  // Keep constrainPosition ref updated
  useEffect(() => {
    constrainPositionRef.current = constrainPosition
  }, [constrainPosition])

  // Handle pointer move during drag - uses refs to avoid stale closures
  const handlePointerMove = useCallback(
    (e: PointerEvent) => {
      if (!enabledRef.current) return

      const { startX, startY, startPosX, startPosY } = dragStateRef.current
      const deltaX = e.clientX - startX
      const deltaY = e.clientY - startY

      const newPosition = constrainPositionRef.current({
        x: startPosX + deltaX,
        y: startPosY + deltaY,
      })

      setPosition(newPosition)
      onDragRef.current?.(newPosition)
    },
    [] // No dependencies - uses refs for latest values
  )

  // Handle pointer up (end drag) - uses refs to avoid stale closures
  const handlePointerUp = useCallback(
    (e: PointerEvent) => {
      setIsDragging(false)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''

      // Remove listeners
      window.removeEventListener('pointermove', handlePointerMove)
      window.removeEventListener('pointerup', handlePointerUp)

      const { startX, startY, startPosX, startPosY } = dragStateRef.current
      const deltaX = e.clientX - startX
      const deltaY = e.clientY - startY

      const finalPosition = constrainPositionRef.current({
        x: startPosX + deltaX,
        y: startPosY + deltaY,
      })

      onDragEndRef.current?.(finalPosition)
    },
    [handlePointerMove] // Only depends on handlePointerMove for removal
  )

  // Handle pointer down (start drag)
  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      // Only handle left mouse button or touch
      if (e.button !== 0 && e.pointerType === 'mouse') return

      // Prevent text selection during drag
      e.preventDefault()

      // Focus the window
      onFocus?.()

      if (!enabled) return

      // Store initial state
      dragStateRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        startPosX: position.x,
        startPosY: position.y,
      }

      setIsDragging(true)
      document.body.style.cursor = 'grabbing'
      document.body.style.userSelect = 'none'

      // Add global listeners
      window.addEventListener('pointermove', handlePointerMove)
      window.addEventListener('pointerup', handlePointerUp)

      onDragStart?.(position)
    },
    [position, handlePointerMove, handlePointerUp, onDragStart, onFocus, enabled]
  )

  // Cleanup listeners on unmount
  useEffect(() => {
    return () => {
      window.removeEventListener('pointermove', handlePointerMove)
      window.removeEventListener('pointerup', handlePointerUp)
    }
  }, [handlePointerMove, handlePointerUp])

  // Container styles using transform for GPU acceleration
  const containerStyle: React.CSSProperties = enabled
    ? {
        position: 'absolute',
        transform: `translate(${position.x}px, ${position.y}px)`,
        willChange: isDragging ? 'transform' : 'auto',
      }
    : {}

  // Drag handle styles
  const dragHandleStyle: React.CSSProperties = enabled
    ? {
        cursor: isDragging ? 'grabbing' : 'grab',
        touchAction: 'none', // Prevent scrolling during drag
      }
    : {}

  return {
    dragHandleProps: {
      onPointerDown: handlePointerDown,
      style: dragHandleStyle,
    },
    containerProps: {
      ref: containerRef,
      style: containerStyle,
    },
    position,
    isDragging,
    setPosition,
  }
}
